import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import "./useSubscriptionExpiryReminder.scss";

const SUBSCRIPTION_TOAST_LOGIN_FLAG = "_show_subscription_expiry_toast";

interface ExpiryStatus {
  dateStr: string;
  diffDays: number;
}

const parseExpiryStatus = (expiryInput: string): ExpiryStatus | null => {
  if (!expiryInput) return null;

  const parsed = new Date(expiryInput);
  if (Number.isNaN(parsed.getTime())) return null;

  const expiryDay = new Date(
    parsed.getFullYear(),
    parsed.getMonth(),
    parsed.getDate()
  );
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round(
    (expiryDay.getTime() - today.getTime()) / 86400000
  );

  const dd = String(parsed.getDate()).padStart(2, "0");
  const mm = String(parsed.getMonth() + 1).padStart(2, "0");
  const yyyy = parsed.getFullYear();

  return {
    dateStr: `${dd}-${mm}-${yyyy}`,
    diffDays,
  };
};

const buildReminderMessage = (status: ExpiryStatus): string | null => {
  if (status.diffDays < 0) {
    return `Your subscription expired on ${status.dateStr}. Please renew to continue uninterrupted access.`;
  }

  if (status.diffDays === 0) {
    return "Your subscription expires today. Please renew to avoid service interruption.";
  }

  if (status.diffDays <= 5) {
    return `Your subscription expires in ${status.diffDays} day${
      status.diffDays === 1 ? "" : "s"
    } (${status.dateStr}). Please renew soon.`;
  }

  return null;
};

export const markSubscriptionReminderForLogin = () => {
  sessionStorage.setItem(SUBSCRIPTION_TOAST_LOGIN_FLAG, "1");
};

export function useSubscriptionExpiryReminder() {
  const { isAuthenticated } = useSelector(({ auth }: any) => auth);
  const licenseExpiryDt = useSelector(
    ({ metadata }: any) => metadata?.dashboardState?.licenseExpiryDt
  );
  const hasShownForCurrentLoginRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      hasShownForCurrentLoginRef.current = false;
      return;
    }

    const shouldShowForThisLogin =
      sessionStorage.getItem(SUBSCRIPTION_TOAST_LOGIN_FLAG) === "1";

    if (!shouldShowForThisLogin || hasShownForCurrentLoginRef.current) {
      return;
    }

    if (!licenseExpiryDt) {
      return;
    }

    const expiryStatus = parseExpiryStatus(licenseExpiryDt);
    if (!expiryStatus) {
      hasShownForCurrentLoginRef.current = true;
      sessionStorage.removeItem(SUBSCRIPTION_TOAST_LOGIN_FLAG);
      return;
    }

    const reminderMessage = buildReminderMessage(expiryStatus);
    hasShownForCurrentLoginRef.current = true;
    sessionStorage.removeItem(SUBSCRIPTION_TOAST_LOGIN_FLAG);

    if (!reminderMessage) {
      return;
    }

    toast.custom(
      (t) => (
        <div className="subscription-reminder-toast" role="alert" aria-live="assertive">
          <span className="subscription-reminder-toast__icon" aria-hidden="true">⚠</span>
          <div className="subscription-reminder-toast__content">
            <div className="subscription-reminder-toast__title">Subscription Reminder</div>
            <div className="subscription-reminder-toast__message">{reminderMessage}</div>
          </div>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              toast.dismiss(t.id);
            }}
            aria-label="Close subscription reminder"
            className="subscription-reminder-toast__close"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            >
              <line x1="4" y1="4" x2="16" y2="16" />
              <line x1="16" y1="4" x2="4" y2="16" />
            </svg>
          </button>
        </div>
      ),
      {
        duration: 10000,
        position: "top-right",
      }
    );
  }, [isAuthenticated, licenseExpiryDt]);
}
